import { DEFAULT_TEAMS_SORT_BY } from "@components/teams/teams-constants";
import { formatUserRoleType } from "@utils/data-display";
import { getHigherRole } from "@utils/member-utils";

/**
 * Gets the initials of a team name to be shown in an avatar
 * E.g. "Sphere Developers" -> "SD"
 *
 * @param teamName The name of a team
 * @returns The initials to show inside an avatar.
 */
export function getTeamAvatarInitials(teamName) {
  if (!teamName) {
    return "";
  }
  const teamNameWords = teamName.split(" ");

  // If the team name has two or more words, return the first letter of the first two words.
  if (teamNameWords.length >= 2) {
    return `${teamNameWords[0][0]}${teamNameWords[1][0]}`.toUpperCase();
  }

  // If the team name has one word, return the first two letters of the word
  return teamName.slice(0, 2).toUpperCase();
}

/**
 * Validates the team name
 * @returns true if the name contains comma or semicolon
 */
export function isValidTeamName(teamName) {
  return !teamName.includes(",") && !teamName.includes(";") && !teamName.includes("|");
}

/**
 * Whether the team is the all-members team, which every workspace gets whatever its subscription
 * is and which it can neither create nor remove. Other auto-managed kinds, such as the email
 * domain teams, only exist where the workspace configured them.
 */
export function isAllMembersTeam(team) {
  return team.category.kind === "all-members";
}

/** The kinds of teams that fill themselves, such as the all-members team or an SSO team */
const AUTO_MANAGED_TEAM_KINDS = new Set(["email-domains", "all-members"]);

/** Whether teams of the given kind fill themselves, so that nobody is invited to them by hand */
export function isAutoManagedTeamKind(kind) {
  return AUTO_MANAGED_TEAM_KINDS.has(kind);
}

/**
 * Whether members can be added to the team by hand. Managed and auto-managed teams fill
 * themselves, so nobody may be invited to them.
 */
export function canAddMembersToTeamManually(team) {
  return !team.category.isManaged && !isAutoManagedTeamKind(team.category.kind);
}

/**
 * Returns the reasons why some members could not be added to a team. The endpoint reports them in
 * `errorData` of a successful response instead of failing the request.
 */
export function getAddMembersToTeamErrors(response) {
  return response.errorData?.map(({
    message
  }) => message) ?? [];
}

/**
 * Whether the shown workspace role of a member may come from a team, because the role of the team
 * is not lower than it. Leaving such a team can lower the role of the member.
 */
export function canRoleComeFromTeam(teamRole, memberRole) {
  return getHigherRole(teamRole, memberRole) === teamRole;
}

/**
 * Whether joining a team raises the workspace role of a member, since a member gets the role of
 * the team whenever it is higher than their own
 */
export function isRoleRaisedByTeam(teamRole, memberRole) {
  return memberRole !== teamRole && canRoleComeFromTeam(teamRole, memberRole);
}

/** Sorts teams by the given sort criteria */
export function sortTeams(teams, sortBy) {
  // Create a shallow copy of the teams array to avoid mutating the original array
  const sortedTeams = [...teams];

  // Pre-compute timestamps for creation date sorting to avoid repeated parsing
  const createdAtMap = sortBy === "creation_date_ascending" || sortBy === "creation_date_descending" ? new Map(teams.map(team => [team.id, new Date(team.createdAt).getTime()])) : null;
  sortedTeams.sort((a, b) => {
    switch (sortBy) {
      case "name_ascending":
        return a.name.localeCompare(b.name);
      case "name_descending":
        return b.name.localeCompare(a.name);
      case "users_ascending":
        return a.memberCount - b.memberCount;
      case "users_descending":
        return b.memberCount - a.memberCount;
      case "creation_date_ascending":
        return createdAtMap.get(a.id) - createdAtMap.get(b.id);
      case "creation_date_descending":
        return createdAtMap.get(b.id) - createdAtMap.get(a.id);
      case "workspace_role_ascending":
        return formatUserRoleType(a.role).localeCompare(formatUserRoleType(b.role));
      case "workspace_role_descending":
        return formatUserRoleType(b.role).localeCompare(formatUserRoleType(a.role));
      default:
        return 0;
    }
  });
  return sortedTeams;
}

/** Converts a SortItem to a TeamsSortBy value */
export function getTeamsSortBy(sortItem) {
  const {
    field,
    sort
  } = sortItem;
  const isAscending = sort === "asc";
  switch (field) {
    case "name":
      return isAscending ? "name_ascending" : "name_descending";
    case "users":
      return isAscending ? "users_ascending" : "users_descending";
    case "creationDate":
      return isAscending ? "creation_date_ascending" : "creation_date_descending";
    case "workspaceRole":
      return isAscending ? "workspace_role_ascending" : "workspace_role_descending";
    default:
      return DEFAULT_TEAMS_SORT_BY;
  }
}